/**
 * What dropping a dragged product means — a pure function, so the rule is
 * tested without simulating a drag.
 *
 * The screen shows products in GROUPS (the root, then each category), over ONE
 * global order. A drop either reorders inside a group or moves the product to
 * another group at the spot it was dropped; both answer with the whole new
 * order, laid out group by group as the screen shows it.
 */

/** A group as the drop sees it: its category (null = root) and its row keys, in order. */
export type DropGroup = { categoryId: string | null; keys: string[] };

export type ProductDrop =
  | { kind: "reorder"; orderedKeys: string[] }
  | { kind: "move"; key: string; categoryId: string | null; orderedKeys: string[] };

/** The droppable id of a group's card — distinct from any row key. */
export function groupDropId(categoryId: string | null): string {
  return `group:${categoryId ?? "root"}`;
}

export function planProductDrop(
  groups: DropGroup[],
  activeKey: string,
  overId: string,
): ProductDrop | null {
  const source = groups.find((g) => g.keys.includes(activeKey));
  if (!source) return null;

  // Dropped on a group's card (an empty one, or its padding) = its end.
  const target =
    groups.find((g) => groupDropId(g.categoryId) === overId) ??
    groups.find((g) => g.keys.includes(overId));
  if (!target) return null;

  if (target === source) {
    const from = source.keys.indexOf(activeKey);
    const to = overId === groupDropId(source.categoryId)
      ? source.keys.length - 1
      : source.keys.indexOf(overId);
    if (from === to) return null;
    const reordered = [...source.keys];
    reordered.splice(from, 1);
    reordered.splice(to, 0, activeKey);
    return {
      kind: "reorder",
      orderedKeys: groups.flatMap((g) => (g === source ? reordered : g.keys)),
    };
  }

  const at = target.keys.includes(overId) ? target.keys.indexOf(overId) : target.keys.length;
  const landed = [...target.keys];
  landed.splice(at, 0, activeKey);
  return {
    kind: "move",
    key: activeKey,
    categoryId: target.categoryId,
    orderedKeys: groups.flatMap((g) =>
      g === source ? g.keys.filter((k) => k !== activeKey) : g === target ? landed : g.keys,
    ),
  };
}
